import { queryOptions } from '@tanstack/react-query';

import { acceptCsrfToken, api, ensureCsrf } from '../client';
import { csrfStore } from '../csrf';
import { endpoints } from '../endpoints';
import { ApiError, type AuthSession, type DeviceBinding, type GameToken, type Viewer } from '../types';
import {
  authSession,
  deviceBinding,
  gameToken,
  viewer as normalizeViewer,
  type WireDevice,
  type WireGameToken,
  type WirePlayerSelf,
  type WireSession,
} from '../wire';

export interface LoginInput {
  identifier: string;
  password: string;
  remember?: boolean;
}

export interface StartRegisterInput {
  username: string;
  email: string;
  locale?: 'zh' | 'en';
  captchaToken: string;
}

export interface RegisterPending {
  pendingVerification: true;
  email: string;
  registrationToken: string;
}

interface WireAuthResult {
  player?: WirePlayerSelf;
  csrf_token?: string;
  pending_verification?: boolean;
  pending_registration?: boolean;
  registration_token?: string | null;
  email_verified?: boolean;
}

export const authQueries = {
  me: () =>
    queryOptions({
      queryKey: ['auth', 'me'] as const,
      queryFn: async (): Promise<Viewer | null> => {
        await ensureCsrf();
        try {
          return normalizeViewer(await api.get<WirePlayerSelf>(endpoints.auth.me()));
        } catch (error) {
          if (error instanceof ApiError && (error.isUnauthorized || error.isBanned)) return null;
          throw error;
        }
      },
      staleTime: 30_000,
      retry: false,
    }),

  sessions: () =>
    queryOptions({
      queryKey: ['account', 'sessions'] as const,
      queryFn: async (): Promise<AuthSession[]> => {
        const payload = await api.get<{ items?: WireSession[] }>(endpoints.account.sessions());
        return (payload.items ?? []).map(authSession);
      },
    }),

  gameTokens: () =>
    queryOptions({
      queryKey: ['account', 'game-tokens'] as const,
      queryFn: async (): Promise<GameToken[]> => {
        const payload = await api.get<{ items?: WireGameToken[] }>(endpoints.account.gameTokens());
        return (payload.items ?? []).map(gameToken);
      },
    }),

  device: () =>
    queryOptions({
      queryKey: ['account', 'device'] as const,
      queryFn: async (): Promise<DeviceBinding> =>
        deviceBinding(await api.get<WireDevice>(endpoints.account.device())),
    }),
};

export const authApi = {
  async login(input: LoginInput): Promise<Viewer> {
    const result = await api.post<WireAuthResult>(endpoints.auth.login(), {
      identifier: input.identifier,
      password: input.password,
      remember: Boolean(input.remember),
    });
    acceptCsrfToken(result.csrf_token);
    return normalizeViewer(result.player);
  },

  async startRegister(input: StartRegisterInput): Promise<RegisterPending> {
    const result = await api.post<WireAuthResult>(endpoints.auth.register(), {
      username: input.username,
      email: input.email,
      locale: input.locale ?? 'zh',
      captcha_token: input.captchaToken,
    });
    return {
      pendingVerification: true,
      email: input.email,
      registrationToken: result.registration_token ?? '',
    };
  },

  sendEmailCode(input: { email: string; purpose: 'register' | 'password_reset'; captchaToken: string }) {
    return api.post<{ sent: boolean; already_sent: boolean }>(endpoints.auth.emailSend(), {
      email: input.email,
      purpose: input.purpose,
      captcha_token: input.captchaToken,
    });
  },

  async verifyRegisterEmail(input: { email: string; code: string }) {
    const result = await api.post<WireAuthResult>(endpoints.auth.emailVerify(), {
      email: input.email,
      code: input.code,
      purpose: 'register',
    });
    return {
      emailVerified: true,
      pendingRegistration: Boolean(result.pending_registration),
      registrationToken: result.registration_token ?? '',
    };
  },

  async setRegisterPassword(input: {
    registrationToken: string;
    password: string;
    passwordConfirmation: string;
    acceptTerms: boolean;
  }) {
    const result = await api.post<WireAuthResult>(endpoints.auth.registerPassword(), {
      registration_token: input.registrationToken,
      password: input.password,
      password_confirmation: input.passwordConfirmation,
      accept_terms: input.acceptTerms,
    });
    return { registrationToken: result.registration_token ?? input.registrationToken };
  },

  async completeRegister(input: {
    registrationToken: string;
    nickname: string;
    birthday?: string | null;
    gender: 'male' | 'female' | 'other' | 'prefer_not_to_say';
    countryCode: string;
  }): Promise<Viewer> {
    const result = await api.post<WireAuthResult>(endpoints.auth.registerComplete(), {
      registration_token: input.registrationToken,
      nickname: input.nickname,
      birthday: input.birthday || null,
      gender: input.gender,
      country_code: input.countryCode,
    });
    acceptCsrfToken(result.csrf_token);
    return normalizeViewer(result.player);
  },

  verifyEmail(input: { email: string; code: string; purpose?: 'register' }) {
    return api.post(endpoints.auth.emailVerify(), {
      email: input.email,
      code: input.code,
      purpose: input.purpose ?? 'register',
    });
  },

  forgotPassword(input: { email: string; captchaToken: string }) {
    return api.post<{ sent: boolean; already_sent: boolean }>(endpoints.auth.passwordForgot(), {
      email: input.email,
      captcha_token: input.captchaToken,
    });
  },

  resetPassword(input: { email: string; code: string; password: string; passwordConfirmation: string }) {
    return api.post(endpoints.auth.passwordReset(), {
      email: input.email,
      code: input.code,
      password: input.password,
      password_confirmation: input.passwordConfirmation,
    });
  },

  async logout(): Promise<void> {
    try {
      await api.post(endpoints.auth.logout());
    } finally {
      csrfStore.clear();
      await ensureCsrf();
    }
  },

  bindGameTicket(ticket: string) {
    return api.post<{ bound: boolean }>(endpoints.auth.gameTicketBind(), { ticket });
  },

  changePassword(input: { currentPassword: string; password: string; passwordConfirmation: string }) {
    return api.post(endpoints.account.password(), {
      current_password: input.currentPassword,
      password: input.password,
      password_confirmation: input.passwordConfirmation,
    });
  },

  changeEmail(input: { currentPassword: string; email: string; code: string }) {
    return api.post(endpoints.account.email(), {
      current_password: input.currentPassword,
      email: input.email,
      code: input.code,
    });
  },

  sendChangeEmailCode(input: { currentPassword: string; email: string; captchaToken: string }) {
    return api.post(endpoints.account.emailSend(), {
      purpose: 'change_email',
      current_password: input.currentPassword,
      email: input.email,
      captcha_token: input.captchaToken,
    });
  },

  sendAccountEmailCode(input: {
    purpose: 'bind_email' | 'verify_email' | 'change_email';
    email?: string;
    currentPassword?: string;
    captchaToken: string;
  }) {
    return api.post(endpoints.account.emailSend(), {
      purpose: input.purpose,
      email: input.email,
      current_password: input.currentPassword,
      captcha_token: input.captchaToken,
    });
  },

  verifyAccountEmail(input: {
    purpose: 'bind_email' | 'verify_email' | 'change_email';
    code: string;
    email?: string;
    currentPassword?: string;
  }) {
    return api.post(endpoints.account.emailVerify(), {
      purpose: input.purpose,
      code: input.code,
      email: input.email,
      current_password: input.currentPassword,
    });
  },

  revokeSession(id: string) {
    return api.delete(endpoints.account.session(id));
  },

  revokeOtherSessions() {
    return api.delete(endpoints.account.sessions());
  },

  revokeGameToken(id: number) {
    return api.delete(endpoints.account.gameToken(id));
  },

  deleteAccount(input: { currentPassword: string }) {
    return api.delete(endpoints.account.self(), { current_password: input.currentPassword, confirm: 'DELETE' });
  },
};
